import {Component, Input, OnDestroy, OnInit, ViewChild,} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {ShiftReportRecordModel, ShiftReportTotalModel} from '../../../../shared/models/shift.model';
import {ShiftReportTableComponent} from '../shift-report-table/shift-report-table.component';
import {ReportsService} from '../../../../data/reports/reports.service';
import moment, {Moment} from 'moment';
import {saveAs} from 'file-saver-es';
import {ConfigurationModel} from '../../../../shared/models/configuration.model';
import {DateFilter} from '../../../../shared/models/DateFilter';
import {ToastService} from '../../../../shared/services/toast.service';
import {MatDialog} from '@angular/material/dialog';
import {
  ShiftReportTableSettingsDialogComponent
} from '../shift-report-table/settings/shift-report-table-settings-dialog/shift-report-table-settings-dialog.component';
import {
  ShiftReportTableSettingsDialogData
} from '../shift-report-table/settings/shift-report-table-settings-dialog/ShiftReportTableSettingsDialogData';
import {ShiftReportColumn} from './ShiftReportColumn';
import {BehaviorSubject, of, Subscription, switchMap} from 'rxjs';
import {ConfigurationService} from '../../../../configuration/configuration.service';
import {MultiSelectFilter} from '../../../../shared/components/multi-select-component';
import {ActivatedRoute, Router} from '@angular/router';
import {CloseableAndFilterableInsight} from '../../closeable-filterable-insight.class';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatIconButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {MatIcon} from '@angular/material/icon';
import {FilterBarComponent} from '../../../../shared/components/filter-bar/filter-bar.component';
import {NgClass} from '@angular/common';
import {FilterButtonComponent} from '../../../../shared/components/buttons/filter-button/filter-button.component';
import {catchError, debounceTime, distinctUntilChanged, filter, tap} from 'rxjs/operators';

interface ReportLoadingParameters {
  from?: moment.Moment
  to?: moment.Moment
  vehicleGroupFilter?: MultiSelectFilter<number>
  vehicleFilter?: MultiSelectFilter<number>
  driverFilter?: MultiSelectFilter<number>
  pageIndex: number
  pageSize: number
  sort?: string
}

@Component({
  imports: [
    MatMenuTrigger,
    MatIconButton,
    MatTooltip,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatIconButton,
    MatTooltip,
    ShiftReportTableComponent,
    FilterBarComponent,
    NgClass,
    FilterButtonComponent,
  ],
  selector: 'app-shift-report',
  styleUrls: ['./shift-report.component.scss'],
  templateUrl: './shift-report.component.html'
})
export class ShiftReportComponent extends CloseableAndFilterableInsight implements OnInit, OnDestroy {

  configuration: ConfigurationModel;
  @Input() isBrowserMobile?: boolean;

  pageSize = 25;
  lastLoadedPageIndex = 0;

  @ViewChild(ShiftReportTableComponent) reportTableComponent: ShiftReportTableComponent;

  dataSource: MatTableDataSource<ShiftReportRecordModel> = new MatTableDataSource([]);
  reportTotal: ShiftReportTotalModel;
  isLoading = false;
  isLoadingMore = false;
  hasMoreDataToLoad = true;
  tableBottomVisible = false;
  isDestroyed = false;
  dateFilter: DateFilter = undefined;
  vehicleGroupFilter: MultiSelectFilter<number> = undefined;
  vehicleFilter: MultiSelectFilter<number> = undefined;
  driverFilter: MultiSelectFilter<number> = undefined;
  columns: ShiftReportColumn[];
  private readonly reportLoadingSubject = new BehaviorSubject<ReportLoadingParameters | null>(null);
  private readonly subscriptions: Subscription[] = [];

  constructor(private readonly reportService: ReportsService,
              private readonly configurationService: ConfigurationService,
              private readonly dialog: MatDialog,
              protected router: Router,
              protected activatedRoute: ActivatedRoute,
              private readonly toast: ToastService) {
    super(router, activatedRoute);
  }

  ngOnInit() {
    this.subscriptions.push(
      this.configurationService.sharedConfigurationModel.subscribe(model => {
        if (model) {
          this.configuration = model;
        }
      })
    );

    let displayedColumnsSetting = localStorage.getItem(ShiftReportColumn.LOCAL_STORAGE_DISPLAYED_COLUMNS);

    // store and use default display columns setting if needed
    if (!displayedColumnsSetting) {
      displayedColumnsSetting = ShiftReportColumn.displayedByDefault.join(',');
      localStorage.setItem(ShiftReportColumn.LOCAL_STORAGE_DISPLAYED_COLUMNS, displayedColumnsSetting);
    }

    this.columns = ShiftReportColumn.columns(displayedColumnsSetting.split(','));

    this.subscriptions.push(
      this.reportLoadingSubject.pipe(
        filter(parameters => parameters && !!parameters.driverFilter && !!parameters.vehicleFilter && !!parameters.vehicleGroupFilter),
        distinctUntilChanged(),
        debounceTime(100),
        tap(parameters => {
          this.isLoadingMore = true
          if (parameters.pageIndex === 0) {
            this.isLoading = true;
            this.dataSource.data = [];
            delete this.reportTotal;
          }
        }),
        switchMap(parameters => {
          const {from, to, vehicleGroupFilter, vehicleFilter, driverFilter, pageIndex, pageSize, sort} = parameters
          return this.reportService.getShiftReport(
            from,
            to,
            vehicleGroupFilter,
            vehicleFilter,
            driverFilter,
            pageIndex,
            pageSize,
            sort).pipe(
              catchError(error => {
                this.toast.longer('Error while fetching data from the server');
                this.isLoading = false
                this.isLoadingMore = false
                return of()
              })
            )
        }),
      ).subscribe(result => {
        if (result.records.number === 0) {
          this.dataSource.data = result.records.content;
        } else {
          const extendedData = [...this.dataSource.data];
          extendedData.push(...result.records.content);
          this.dataSource.data = extendedData;
        }
        this.reportTotal = result.total;
        this.lastLoadedPageIndex = result.records.number;
        this.hasMoreDataToLoad = !result.records.last

        this.isLoading = false;
        this.isLoadingMore = false;
        if (this.hasMoreDataToLoad) {
          this.triggerCheckForLoadMoreData()
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.isDestroyed = true;
  }

  showSettingsDialog() {
    const dialogRef = this.dialog.open(ShiftReportTableSettingsDialogComponent, {
      data: new ShiftReportTableSettingsDialogData(
        this.columns.map(value => ({...value}))
      ),
      width: '300px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != null) {
        const data = result as ShiftReportTableSettingsDialogData;
        localStorage.setItem(
          ShiftReportColumn.LOCAL_STORAGE_DISPLAYED_COLUMNS,
          data.columns.filter(value => value.displayed).map(value => value.id).join(',')
        );
        this.columns = data.columns;
      }
    });
  }

  dateFilterChanged(dateFilter: DateFilter) {
    this.dateFilter = dateFilter;
    this.loadReport();
  }

  vehicleGroupFilterChanged(vehicleGroupFilter: MultiSelectFilter<number>) {
    this.vehicleGroupFilter = vehicleGroupFilter;
    this.loadReport();
  }

  vehicleFilterChanged(vehicleFilter: MultiSelectFilter<number>) {
    this.vehicleFilter = vehicleFilter;
    this.loadReport();
  }

  driverFilterChanged(driverFilter: MultiSelectFilter<number>) {
    this.driverFilter = driverFilter;
    this.loadReport();
  }

  loadReport() {
    if (this.reportTableComponent) {
      this.reportLoadingSubject.next({
        from: this.dateFilter.from ? this.dateFilter.from : null,
        to: this.dateFilter.to ? this.dateFilter.to : null,
        vehicleGroupFilter: this.vehicleGroupFilter,
        vehicleFilter: this.vehicleFilter,
        driverFilter: this.driverFilter,
        pageIndex: 0,
        pageSize: this.pageSize,
        sort: this.reportTableComponent.getSort(),
      })
    }
  }

  tableBottomVisibleChange(tableBottomVisible: boolean) {
    this.tableBottomVisible = tableBottomVisible
    if (!this.isLoading && !this.isLoadingMore && this.tableBottomVisible && this.hasMoreDataToLoad) {
      this.triggerCheckForLoadMoreData();
    }
  }

  loadMoreReportData() {
    if (this.isLoading || this.isLoadingMore || this.isDestroyed) {
      return
    }
    this.reportLoadingSubject.next({
      from: this.dateFilter.from ? this.dateFilter.from : null,
      to: this.dateFilter.to ? this.dateFilter.to : null,
      vehicleGroupFilter: this.vehicleGroupFilter,
      vehicleFilter: this.vehicleFilter,
      driverFilter: this.driverFilter,
      pageIndex: this.lastLoadedPageIndex + 1,
      pageSize: this.pageSize,
      sort: this.reportTableComponent.getSort(),
    })
  }

  loadCSVExportFile() {
    if (this.dataSource.data?.length && !this.isLoading) {
      this.isLoading = true
      this.reportService.getShiftReportCSV(
        this.dateFilter.from ? this.dateFilter.from : null,
        this.dateFilter.to ? this.dateFilter.to : null,
        this.vehicleGroupFilter,
        this.vehicleFilter,
        this.driverFilter
      ).then(response => {
        const blob = new Blob([response], {type: 'csv'});
        saveAs(blob, `${this._fileNameWithDateRange(
          this.dateFilter.from ? this.dateFilter.from : null,
          this.dateFilter.to ? this.dateFilter.to : null
        )}.csv`);
      }).catch(error => {
        this.toast.longer('Error while fetching data from the server');
        console.error(error);
      }).finally(() => this.isLoading = false);
    }
  }

  loadPDFExportFile() {
    if (this.dataSource.data?.length && !this.isLoading) {
      this.isLoading = true
      this.reportService.getShiftReportPDF(
        this.dateFilter.from ? this.dateFilter.from : null,
        this.dateFilter.to ? this.dateFilter.to : null,
        this.vehicleGroupFilter,
        this.vehicleFilter,
        this.driverFilter,
        this.columns.filter(value => !value.displayed).map(value => value.id),
      ).then(response => {
        const blob = new Blob([response], {type: 'pdf'});
        saveAs(blob, `${this._fileNameWithDateRange(
          this.dateFilter.from ? this.dateFilter.from : null,
          this.dateFilter.to ? this.dateFilter.to : null
        )}.pdf`);
      }).catch(error => {
        this.toast.longer('Error while fetching data from the server');
        console.error(error);
      }).finally(() => this.isLoading = false);
    }
  }

  private _fileNameWithDateRange(from: Moment, to: Moment): string {
    const fromStr = from ? `_${from.format('MM-DD-YYYY')}` : '';
    const toStr = (to || moment()).format('MM-DD-YYYY');
    return `shift_report${fromStr}_${toStr}`;
  }

  private triggerCheckForLoadMoreData() {
    setTimeout(() => {
      if (this.tableBottomVisible) {
        this.loadMoreReportData()
      }
    }, 100)
  }
}
